import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["trigger", "content", "helpPanel"]
  static values = { 
    position: String,
    delay: Number,
    persistent: Boolean
  }

  connect() {
    this.initializeHelp()
    this.setupEventListeners()
  }

  initializeHelp() {
    this.helpData = {
      'data-source': {
        title: 'Data Source Selection',
        content: 'Choose the primary data source for your report. This determines what fields and information will be available for selection.',
        tips: [
          'Start with the data source that contains your most important information',
          'You can join related data sources later in the process',
          'Different data sources may have different available fields'
        ]
      },
      'field-selection': {
        title: 'Field Selection',
        content: 'Select the specific data fields you want to include in your report. You can search for fields or browse by category.',
        tips: [
          'Use the search box to quickly find specific fields',
          'Drag fields to the right panel or click checkboxes to select them',
          'Selected fields can be reordered by dragging'
        ]
      },
      'field-configuration': {
        title: 'Field Configuration',
        content: 'Customize how your selected fields appear in the report, including display names, formatting, and sorting options.',
        tips: [
          'Click "Configure" to customize field display names and formatting',
          'Drag fields to reorder them in your report',
          'Use the remove button to deselect fields you no longer need'
        ]
      },
      'filters': {
        title: 'Report Filters',
        content: 'Add filters to narrow down your data and focus on specific information that matters to you.',
        tips: [
          'Filters help reduce the amount of data in your report',
          'You can combine multiple filters with AND/OR logic',
          'Date filters support ranges and relative dates'
        ]
      },
      'charts': {
        title: 'Chart Visualization',
        content: 'Create visual representations of your data with various chart types and customization options.',
        tips: [
          'Choose chart types that best represent your data',
          'Pie charts work well for categorical data',
          'Line charts are great for showing trends over time',
          'Bar charts are effective for comparing values'
        ]
      },
      'preview': {
        title: 'Report Preview',
        content: 'See a preview of your report before generating the full version. This helps you verify the data and layout.',
        tips: [
          'The preview shows the first 10 rows of your data',
          'Use "Refresh" to update the preview after making changes',
          'Generate the full report when you\'re satisfied with the preview'
        ]
      },
      'export': {
        title: 'Export Options',
        content: 'Export your report in various formats including PDF, Excel, and CSV. You can also email reports directly.',
        tips: [
          'PDF exports include charts and formatting',
          'Excel exports allow further data manipulation',
          'CSV exports are great for importing into other systems',
          'Email delivery can be scheduled for regular reports'
        ]
      },
      'templates': {
        title: 'Report Templates',
        content: 'Save your report configuration as a template for future use and share templates with team members.',
        tips: [
          'Templates save time when creating similar reports',
          'Shared templates help maintain consistency across your team',
          'You can create variations of existing templates'
        ]
      }
    }
    
    this.positionValue = this.positionValue || 'top'
    this.delayValue = this.delayValue || 500
    this.persistentValue = this.persistentValue || false
    
    this.isVisible = false
    this.hideTimeout = null
  }

  setupEventListeners() {
    // Set up hover events for non-persistent help
    if (!this.persistentValue) {
      this.element.addEventListener('mouseenter', this.showHelp.bind(this))
      this.element.addEventListener('mouseleave', this.hideHelp.bind(this))
    }
    
    // Set up click events for persistent help
    if (this.persistentValue) {
      this.element.addEventListener('click', this.toggleHelp.bind(this))
    }
    
    // Set up focus events for keyboard accessibility
    this.element.addEventListener('focus', this.showHelp.bind(this))
    this.element.addEventListener('blur', this.hideHelp.bind(this))
    
    // Set up keyboard events
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
    
    // Close help when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  showHelp(event) {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    
    if (this.isVisible) return
    
    const helpKey = this.element.dataset.helpKey
    const helpData = this.helpData[helpKey]
    
    if (!helpData) {
      console.warn(`No help data found for key: ${helpKey}`)
      return
    }
    
    // Create help content if it doesn't exist
    if (!this.helpContent) {
      this.createHelpContent(helpData)
    }
    
    // Position and show help
    this.positionHelp()
    this.helpContent.classList.add('active')
    this.isVisible = true
    
    // Announce to screen readers
    this.announceToScreenReader(`Help available: ${helpData.title}. ${helpData.content}`)
    
    // Track help usage
    this.trackHelpUsage(helpKey, 'shown')
  }

  hideHelp(event) {
    if (!this.isVisible || this.persistentValue) return
    
    // Add delay before hiding to allow mouse movement to help content
    this.hideTimeout = setTimeout(() => {
      if (this.helpContent) {
        this.helpContent.classList.remove('active')
      }
      this.isVisible = false
    }, 200)
  }

  toggleHelp(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.isVisible) {
      this.forceHideHelp()
    } else {
      this.showHelp(event)
    }
  }

  forceHideHelp() {
    if (this.helpContent) {
      this.helpContent.classList.remove('active')
    }
    this.isVisible = false
    
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  createHelpContent(helpData) {
    this.helpContent = document.createElement('div')
    this.helpContent.className = 'contextual-help-content'
    this.helpContent.setAttribute('role', 'tooltip')
    this.helpContent.setAttribute('aria-live', 'polite')
    
    // Create help content HTML
    const tipsHtml = helpData.tips ? helpData.tips.map(tip => 
      `<li>${tip}</li>`
    ).join('') : ''
    
    this.helpContent.innerHTML = `
      <div class="help-header">
        <h6 class="help-title">${helpData.title}</h6>
        ${this.persistentValue ? '<button type="button" class="btn-close" aria-label="Close help"></button>' : ''}
      </div>
      <div class="help-body">
        <p class="help-description">${helpData.content}</p>
        ${tipsHtml ? `
          <div class="help-tips">
            <strong>Tips:</strong>
            <ul>${tipsHtml}</ul>
          </div>
        ` : ''}
      </div>
      ${this.persistentValue ? `
        <div class="help-footer">
          <button type="button" class="btn btn-sm btn-outline-primary help-more-info">
            More Info
          </button>
        </div>
      ` : ''}
    `
    
    // Add event listeners to help content
    if (this.persistentValue) {
      const closeBtn = this.helpContent.querySelector('.btn-close')
      if (closeBtn) {
        closeBtn.addEventListener('click', this.forceHideHelp.bind(this))
      }
      
      const moreInfoBtn = this.helpContent.querySelector('.help-more-info')
      if (moreInfoBtn) {
        moreInfoBtn.addEventListener('click', () => {
          this.showDetailedHelp(helpData)
        })
      }
    }
    
    // Prevent help from hiding when hovering over it
    this.helpContent.addEventListener('mouseenter', () => {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout)
        this.hideTimeout = null
      }
    })
    
    this.helpContent.addEventListener('mouseleave', () => {
      if (!this.persistentValue) {
        this.hideHelp()
      }
    })
    
    // Add to DOM
    document.body.appendChild(this.helpContent)
  }

  positionHelp() {
    if (!this.helpContent) return
    
    const triggerRect = this.element.getBoundingClientRect()
    const helpRect = this.helpContent.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    let left, top
    
    // Calculate position based on preferred position
    switch (this.positionValue) {
      case 'top':
        left = triggerRect.left + (triggerRect.width / 2) - (helpRect.width / 2)
        top = triggerRect.top - helpRect.height - 10
        break
      case 'bottom':
        left = triggerRect.left + (triggerRect.width / 2) - (helpRect.width / 2)
        top = triggerRect.bottom + 10
        break
      case 'left':
        left = triggerRect.left - helpRect.width - 10
        top = triggerRect.top + (triggerRect.height / 2) - (helpRect.height / 2)
        break
      case 'right':
        left = triggerRect.right + 10
        top = triggerRect.top + (triggerRect.height / 2) - (helpRect.height / 2)
        break
      default:
        left = triggerRect.right + 10
        top = triggerRect.top
    }
    
    // Adjust if help would go outside viewport
    if (left < 10) left = 10
    if (left + helpRect.width > viewportWidth - 10) {
      left = viewportWidth - helpRect.width - 10
    }
    if (top < 10) top = 10
    if (top + helpRect.height > viewportHeight - 10) {
      top = viewportHeight - helpRect.height - 10
    }
    
    // Apply position
    this.helpContent.style.left = `${left}px`
    this.helpContent.style.top = `${top}px`
    
    // Add position class for arrow styling
    this.helpContent.setAttribute('data-position', this.positionValue)
  }

  showDetailedHelp(helpData) {
    // Create detailed help modal
    const modal = document.createElement('div')
    modal.className = 'modal fade'
    modal.setAttribute('tabindex', '-1')
    modal.setAttribute('aria-labelledby', 'detailedHelpModalLabel')
    modal.setAttribute('aria-hidden', 'true')
    
    modal.innerHTML = `
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="detailedHelpModalLabel">
              <i class="fas fa-question-circle me-2"></i>
              ${helpData.title}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="help-content-detailed">
              <p class="lead">${helpData.content}</p>
              
              ${helpData.tips ? `
                <div class="mt-4">
                  <h6><i class="fas fa-lightbulb me-2"></i>Tips & Best Practices</h6>
                  <ul class="list-unstyled">
                    ${helpData.tips.map(tip => `
                      <li class="mb-2">
                        <i class="fas fa-check-circle text-success me-2"></i>
                        ${tip}
                      </li>
                    `).join('')}
                  </ul>
                </div>
              ` : ''}
              
              <div class="mt-4">
                <h6><i class="fas fa-keyboard me-2"></i>Keyboard Shortcuts</h6>
                <div class="row">
                  <div class="col-md-6">
                    <ul class="list-unstyled small">
                      <li><kbd>Tab</kbd> - Navigate between elements</li>
                      <li><kbd>Enter</kbd> - Activate buttons/links</li>
                      <li><kbd>Space</kbd> - Toggle checkboxes</li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <ul class="list-unstyled small">
                      <li><kbd>Ctrl+S</kbd> - Save template</li>
                      <li><kbd>Ctrl+R</kbd> - Refresh preview</li>
                      <li><kbd>F1</kbd> - Show help</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-primary" onclick="window.open('/help/report-builder', '_blank')">
              <i class="fas fa-external-link-alt me-1"></i>
              Full Documentation
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(modal)
    
    // Show modal
    const bootstrapModal = new Modal(modal)
    bootstrapModal.show()
    
    // Clean up when modal is hidden
    modal.addEventListener('hidden.bs.modal', () => {
      modal.remove()
    })
    
    // Track detailed help usage
    this.trackHelpUsage(this.element.dataset.helpKey, 'detailed_view')
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isVisible) {
      this.forceHideHelp()
    } else if (event.key === 'F1') {
      event.preventDefault()
      this.showHelp(event)
    }
  }

  handleOutsideClick(event) {
    if (this.isVisible && this.persistentValue && 
        !this.element.contains(event.target) && 
        (!this.helpContent || !this.helpContent.contains(event.target))) {
      this.forceHideHelp()
    }
  }

  announceToScreenReader(message) {
    // Create or update announcement region
    let announcer = document.getElementById('help-announcer')
    if (!announcer) {
      announcer = document.createElement('div')
      announcer.id = 'help-announcer'
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      announcer.className = 'sr-only'
      document.body.appendChild(announcer)
    }
    
    announcer.textContent = message
  }

  trackHelpUsage(helpKey, action) {
    // Track help usage for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'contextual_help', {
        help_key: helpKey,
        action: action
      })
    }
    
  }

  disconnect() {
    if (this.helpContent && this.helpContent.parentNode) {
      this.helpContent.parentNode.removeChild(this.helpContent)
    }
    
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
  }

  // Static method to add help to elements
  static addHelpToElement(element, helpKey, options = {}) {
    element.setAttribute('data-controller', 'contextual-help')
    element.setAttribute('data-help-key', helpKey)
    
    if (options.position) {
      element.setAttribute('data-contextual-help-position-value', options.position)
    }
    if (options.persistent) {
      element.setAttribute('data-contextual-help-persistent-value', 'true')
    }
    if (options.delay) {
      element.setAttribute('data-contextual-help-delay-value', options.delay.toString())
    }
    
    // Add help icon if requested
    if (options.showIcon !== false) {
      const helpIcon = document.createElement('i')
      helpIcon.className = 'fas fa-question-circle text-muted ms-1'
      helpIcon.style.cursor = 'help'
      element.appendChild(helpIcon)
    }
  }
}